import React, { useState, useRef } from 'react';
import { MapPin, Edit2, X, Check, Camera, Loader } from 'lucide-react';
import { interests } from '../data/interests';
import { uploadProfilePicture } from '../firebase/storage';

const ProfileView = ({ 
  currentUser, 
  profileEdit, 
  editedProfile, 
  setEditedProfile,
  startEditProfile,
  saveProfile,
  setProfileEdit,
  setCurrentUser
}) => {
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  
  if (!currentUser) return null;

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    setUploadError('');

    const result = await uploadProfilePicture(currentUser.id, file);

    if (result.success) {
      // Update local state
      setCurrentUser({ ...currentUser, avatar: result.url });
      if (profileEdit) {
        setEditedProfile({ ...editedProfile, avatar: result.url });
      }
    } else {
      setUploadError(result.error);
    }

    setUploadingPhoto(false);
  };
  
  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Profile Header */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 p-8 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="relative bg-white/20 rounded-full p-2">
                <img src={currentUser.avatar} alt={currentUser.name} className="w-20 h-20 rounded-full object-cover" />
                {profileEdit && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="absolute bottom-0 right-0 bg-white text-red-600 rounded-full p-2 hover:bg-red-50 transition-colors shadow-lg disabled:opacity-50"
                    title="Upload new profile picture"
                  >
                    {uploadingPhoto ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4" />
                    )}
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold mb-2">
                  {profileEdit ? (
                    <input
                      type="text"
                      value={editedProfile.name}
                      onChange={(e) => setEditedProfile({...editedProfile, name: e.target.value})}
                      className="bg-white/20 border border-white/40 rounded-lg px-3 py-1 text-white placeholder-white/70"
                    />
                  ) : (
                    currentUser.name
                  )}
                </h1>
                <p className="flex items-center text-red-100">
                  <MapPin className="w-4 h-4 mr-1" />
                  {currentUser.location}
                </p>
                {uploadError && (
                  <p className="text-red-200 text-sm mt-1">{uploadError}</p>
                )}
              </div>
            </div>
            
            {!profileEdit ? (
              <button
                onClick={startEditProfile}
                className="flex items-center space-x-2 px-4 py-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                <span>Edit Profile</span>
              </button>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={saveProfile}
                  className="p-2 bg-green-500 rounded-full hover:bg-green-600"
                >
                  <Check className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setProfileEdit(false)}
                  className="p-2 bg-red-700 rounded-full hover:bg-red-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Profile Content */}
        <div className="p-8">
          {/* Bio Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">About Me</h3>
            {profileEdit ? (
              <textarea
                value={editedProfile.bio}
                onChange={(e) => setEditedProfile({...editedProfile, bio: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows="3"
                placeholder="Tell others about yourself..."
              />
            ) : (
              <p className="text-gray-700">{currentUser.bio}</p>
            )}
          </div>

          {/* Interests Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Interests</h3>
            <div className="flex flex-wrap gap-3">
              {interests.map(interest => {
                const Icon = interest.icon;
                const isSelected = profileEdit 
                  ? editedProfile.interests?.includes(interest.id)
                  : currentUser.interests?.includes(interest.id);
                
                return profileEdit ? (
                  <button
                    key={interest.id}
                    onClick={() => {
                      const newInterests = editedProfile.interests?.includes(interest.id)
                        ? editedProfile.interests.filter(i => i !== interest.id)
                        : [...(editedProfile.interests || []), interest.id];
                      setEditedProfile({...editedProfile, interests: newInterests});
                    }}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full border-2 transition-all ${
                      isSelected
                        ? 'border-red-500 bg-red-50 text-red-600'
                        : 'border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{interest.label}</span>
                  </button>
                ) : isSelected ? (
                  <span
                    key={interest.id}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-50 text-red-600 rounded-full"
                  >
                    <Icon className="w-4 h-4" />
                    <span>{interest.label}</span>
                  </span>
                ) : null;
              })}
            </div>
          </div>

          {/* Preferences Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Meeting Preferences</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2">
                <span className="text-gray-700">Who I'd like to meet:</span>
                {profileEdit ? (
                  <select
                    value={editedProfile.genderPreference}
                    onChange={(e) => setEditedProfile({...editedProfile, genderPreference: e.target.value})}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  >
                    <option value="all">Everyone</option>
                    <option value="women">Women only</option>
                    <option value="men">Men only</option>
                  </select>
                ) : (
                  <span className="px-4 py-1 bg-gray-100 text-gray-700 rounded-full">
                    {currentUser.genderPreference === 'all' ? 'Everyone' : 
                     currentUser.genderPreference === 'women' ? 'Women only' : 'Men only'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Social Links */}
          {(currentUser.socials?.instagram || currentUser.socials?.linkedin) && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Connect With Me</h3>
              <div className="flex space-x-4">
                {currentUser.socials.instagram && (
                  <a href="#" className="text-gray-600 hover:text-red-600">
                    Instagram: @{currentUser.socials.instagram}
                  </a>
                )}
                {currentUser.socials.linkedin && (
                  <a href="#" className="text-gray-600 hover:text-red-600">
                    LinkedIn
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Account Info */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>Member since: {new Date().toLocaleDateString()}</span>
              <span>Email: {currentUser.email}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileView;